import React, { useEffect, useRef, useState } from 'react';
import './new-question-form.styles.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faSquare } from '@fortawesome/free-regular-svg-icons';
import { faArrowDown, faArrowUp, faArrowUpShortWide, faInfoCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ModalAlert } from '@/components/SHARED/modal/modal.component';

const NewQuestionForm = ({
  questionSetData,
  setShowInfoModal,
  saveSetData,
  setCreateQuestionOpen,
  questionToEdit,
  setAlertMessage
}) => {

  const [formData, setFormData] = useState({});
  const [tempOption, setTempOption] = useState('');
  const [tempLikertValue, setTempLikertValue] = useState('');
  const [tempLikertLabel, setTempLikertLabel] = useState('');
  const dataRef = useRef({});

  useEffect(() => {
    if (questionToEdit) {
      dataRef.current = { ...questionToEdit };
      setFormData(dataRef.current);
    }
  }, [questionToEdit]);

  function saveData(key, value) {
    if (key === 'title') {
      value = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 20);
    }

    if (key === 'type' && value !== formData.type) {
      const newData = { type: value };
      if (value === 'likert_scale') {
        newData.likertOptions = [
          { id: '1_likert', value: 1, label: 'Disagree' },
          { id: '2_likert', value: 2, label: 'Somewhat Agree' },
          { id: '3_likert', value: 3, label: 'Agree' },
          { id: '4_likert', value: 4, label: 'Strongly Agree' },
        ];
      }
      dataRef.current = newData;
      setFormData(dataRef.current);
      return;
    }

    dataRef.current = {
      ...dataRef.current,
      ...{ [key]: value }
    };
    setFormData(dataRef.current);
    // console.log(dataRef.current);
  }

  function addQuestionOption(value) {
    if (!value) return;
    const newOption = {
      id: new Date().getTime() + '_opt',
      text: value
    };
    dataRef.current = {
      ...dataRef.current,
      options: [
        ...(dataRef.current.options || []),
        newOption
      ]
    };
    setFormData(dataRef.current);
    setTempOption('');
  }

  function moveOption(direction, optionId) {
    const currentOptions = formData.options || [];
    const optionIndex = currentOptions.findIndex(opt => opt.id === optionId);
    if (optionIndex === -1) return;

    if (direction === "up" && optionIndex > 0) {
      const newOptions = [...currentOptions];
      [newOptions[optionIndex - 1], newOptions[optionIndex]] = [newOptions[optionIndex], newOptions[optionIndex - 1]];
      dataRef.current = {
        ...dataRef.current,
        options: newOptions
      };
      setFormData(dataRef.current);
    }

    if (direction === "down" && optionIndex < currentOptions.length - 1) {
      const newOptions = [...currentOptions];
      [newOptions[optionIndex + 1], newOptions[optionIndex]] = [newOptions[optionIndex], newOptions[optionIndex + 1]];
      dataRef.current = {
        ...dataRef.current,
        options: newOptions
      };
      setFormData(dataRef.current);
    }
  }

  function removeOption(optionId) {
    const currentOptions = formData.options || [];
    const newOptions = currentOptions.filter(opt => opt.id !== optionId);
    dataRef.current = {
      ...dataRef.current,
      options: newOptions
    };
    setFormData(dataRef.current);
  }

  function addLikertOption() {
    if (!tempLikertValue || !tempLikertLabel) return;
    const num = Number(tempLikertValue);
    const existing = dataRef.current.likertOptions || [];
    if (existing.some(opt => opt.value === num)) {
      setAlertMessage(`A scale point with value ${num} already exists.`);
      return;
    }
    const newOption = { id: new Date().getTime() + '_likert', value: num, label: tempLikertLabel };
    const newOptions = [...existing, newOption].sort((a, b) => a.value - b.value);
    dataRef.current = { ...dataRef.current, likertOptions: newOptions };
    setFormData(dataRef.current);
    setTempLikertValue('');
    setTempLikertLabel('');
  }

  function removeLikertOption(optionId) {
    const newOptions = (dataRef.current.likertOptions || []).filter(opt => opt.id !== optionId);
    dataRef.current = { ...dataRef.current, likertOptions: newOptions };
    setFormData(dataRef.current);
  }

  function saveQuestionToSet() {
    // console.log('Save Question to Set', formData);
    const tempData = { ...dataRef.current };
    if (
      !tempData.type ||
      !tempData.title ||
      !tempData.prompt ||
      (
        (
          tempData.type === 'multiple_choice' ||
          tempData.type === 'checkbox'
        ) &&
        (
          !tempData.options || tempData.options.length < 2
        )
      ) ||
      (
        tempData.type === 'likert_scale' &&
        (!tempData.likertOptions || tempData.likertOptions.length < 2)
      ) ||
      (
        questionSetData.comparison &&
        !tempData.comparisonWeight
      )
    ) {
      setAlertMessage('Please provide a valid question type, title, prompt, weighting for comparison questions if applicable, and at least two options for multiple choice/checkbox questions.');
      return;
    }
    const tempQuestions = questionSetData.questions || {};
    if (!tempData.id) {
      tempData.id = new Date().getTime() + '_q';
      const existingCount = Object.keys(tempQuestions).length;
      tempData.order = existingCount + 1;
    }
    saveSetData('questions', { ...tempQuestions, ...{ [tempData.id]: tempData } });
    dataRef.current = {};
    setFormData(dataRef.current);
  }

  function closeForm() {
    dataRef.current = {};
    setFormData(dataRef.current);
    setCreateQuestionOpen(false);
  }

  return (
    <div className='form-builder_new-question-form'>
      <div className='form-builder_field small short'>
        <label className='with-icon'>
          Choose Question Type
          <div
            className='info-icon'
            onClick={() => setShowInfoModal('questionTypes')}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </div>
        </label>
        <select
          value={formData.type ?? ''}
          onChange={e => saveData('type', e.target.value)}>
          <option value=''>Select Type...</option>
          <option value="short">Short Answer</option>
          <option value="long">Paragraph</option>
          <option value="multiple_choice">Multiple Choice</option>
          <option value="checkbox">Checkbox</option>
          <option value="likert_scale">Likert Scale</option>
          {/* <option value="upload">Upload File</option> */}
        </select>
      </div>

      {
        (formData.type) &&
        <>
          <div className='form-builder_field small med'>
            <label className='with-icon'>
              Short Title
              <div
                className='info-icon'
                onClick={() => setShowInfoModal('shortTitle')}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </div>
            </label>
            <input
              type="text"
              value={formData.title ?? ''}
              onChange={e => saveData('title', e.target.value)} />
          </div>

          <div className="form-builder_field-checkbox small">
            <label className='with-icon'>
              Required Question
              <div
                className='info-icon'
                onClick={() => setShowInfoModal('requiredQuestion')}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </div>
            </label>
            <input
              type="checkbox"
              checked={formData.required ?? false}
              onChange={e => saveData('required', e.target.checked)} />
          </div>
          <div style={{ height: '1rem' }}></div>
          <div className='form-builder_field small'>
            <label>Question Prompt</label>
            <textarea
              value={formData.prompt ?? ''}
              onChange={e => saveData('prompt', e.target.value)} >
            </textarea>
          </div>
          {
            (formData.type === 'multiple_choice') &&
            <div className='form-builder_field small'>
              <label>Display Format</label>
              <select
                value={formData.displayFormat ?? ''}
                onChange={e => saveData('displayFormat', e.target.value)} >
                <option value=''>Choose a Format...</option>
                <option value='radio'>Radio Buttons (all options displayed)</option>
                <option value='select'>Select via Dropdown</option>
              </select>
            </div>
          }
        </>
      }
      {
        formData.type === 'upload' &&
        <div className='form-builder_field small'>
          <label>Allowed File Types (comma separated, no spaces, e.g. "pdf, docx, png")</label>
          <input
            type="text"
            value={formData.allowedFileTypes ?? ''}
            onChange={e => saveData('allowedFileTypes', e.target.value)} />
        </div>
      }
      {
        (formData.type === 'multiple_choice' || formData.type === 'checkbox') &&
        <div className='multiple-options'>
          <div className='form-builder_field small no-margin'>
            <label>Add Response Option</label>
            <input
              type="text"
              value={tempOption}
              onChange={e => setTempOption(e.target.value)} />

          </div>
          <div className='form-builder_buttons'>
            <button
              className='form-builder_button med-btn'
              onClick={() => addQuestionOption(tempOption)}>
              Save Option
            </button>
          </div>
          <div className='form-builder_added-options'>
            {
              (formData.options) &&
              formData.options.map((opt, idx) => (
                <div key={opt.id} className='form-builder_added-option'>
                  {
                    <FontAwesomeIcon icon={
                      formData.type === 'multiple_choice'
                        ?
                        faCircle
                        :
                        faSquare
                    } />
                  }
                  <div className='up-down-buttons'>
                    <button
                      className={`form-builder_button text-only no-pad ${idx === 0 ? 'disabled' : ''}`}
                      onClick={() => moveOption("up", opt.id)}>
                      <FontAwesomeIcon
                        icon={faArrowUp} />
                    </button>
                    <button
                      className={`form-builder_button text-only no-pad ${idx === (formData.options.length - 1)
                        ? 'disabled'
                        : ''
                        }`
                      }
                      onClick={() => moveOption("down", opt.id)}>
                      <FontAwesomeIcon
                        icon={faArrowDown} />
                    </button>

                  </div>
                  <div className='added-option-text'>
                    {opt.text}
                  </div>
                  <button
                    className={`form-builder_button text-only no-pad`}
                    onClick={() => removeOption(opt.id)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))
            }
          </div>
        </div>
      }
      {
        formData.type === 'likert_scale' &&
        <div className='likert-options'>
          <div className='likert-options_current'>
            <label>Scale Points</label>
            {
              (formData.likertOptions || []).map(opt => (
                <div key={opt.id} className='likert-option-row'>
                  <span className='likert-value'>{opt.value}</span>
                  <span className='likert-separator'>—</span>
                  <span className='likert-label'>{opt.label}</span>
                  <button
                    className='form-builder_button text-only no-pad'
                    onClick={() => removeLikertOption(opt.id)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))
            }
          </div>
          <div className='likert-options_add'>
            <label>Add Scale Point</label>
            <div className='form-builder_field small no-margin likert-add-inputs'>
              <input
                type="number"
                placeholder="Number"
                value={tempLikertValue}
                onChange={e => setTempLikertValue(e.target.value)} />
              <input
                type="text"
                placeholder="Descriptor (e.g. Strongly Agree)"
                value={tempLikertLabel}
                onChange={e => setTempLikertLabel(e.target.value)} />
            </div>
            <div className='form-builder_buttons' style={{ marginTop: '.5rem' }}>
              <button
                className='form-builder_button med-btn'
                onClick={() => addLikertOption()}>
                Add Point
              </button>
            </div>
          </div>
        </div>
      }
      <div className='form-builder_buttons'>
        <button
          className='form-builder_button med-btn secondary-btn'
          onClick={() => closeForm()}>
          Cancel
        </button>
        <button
          className='form-builder_button med-btn'
          onClick={() => saveQuestionToSet()}>
          Save Question
        </button>

      </div>
    </div>
  )
}

export default NewQuestionForm